import { Box, Card, CardContent, LinearProgress, Skeleton, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Currency } from "src/components/with-prefix";
import useAuth from "src/hooks/useAuth";
import useLocales from "src/hooks/useLocales";
import { fNumberCents } from "src/utils/formatNumber";

/**
 * Breakdown delle commissioni per tipo bonus sul periodo/filtri attivi.
 * Risponde alla domanda "da dove arrivano i miei guadagni?".
 *
 * Mostra 3 tipi MLM per default. Mother bonus (motherBonus +
 * motherResidualBonus) sono mostrati solo a summusadmin: per gli altri
 * utenti questi tipi non esistono in DB (logica FPI: bonus madre / sfuso
 * residuo confluiscono su user_id=1).
 */
const TYPE_META = [
  { key: "direct_unilevel_bonus",   labelKey: "userFinancial.eWallet.directUnilevelBonus",   color: "#2e7d32", emoji: "🟢" },
  { key: "indirect_unilevel_bonus", labelKey: "userFinancial.eWallet.indirectUnilevelBonus", color: "#1976d2", emoji: "🔵" },
  { key: "global_pool_bonus",       labelKey: "userFinancial.eWallet.globalPoolBonus",       color: "#7b1fa2", emoji: "🟣" },
];

const ADMIN_TYPE_META = [
  { key: "mother_bonus",          labelKey: "userFinancial.eWallet.motherBonus",         color: "#ed6c02", emoji: "🟠" },
  { key: "mother_residual_bonus", labelKey: "userFinancial.eWallet.motherResidualBonus", color: "#c2185b", emoji: "🩷" },
];

const BreakdownByType = ({ breakdown, total, isLoading }) => {
  const { translate } = useLocales();
  const theme = useTheme();
  const { user } = useAuth();
  const isSummusAdmin = Number(user?.id) === 1;

  const allTypes = isSummusAdmin ? [...TYPE_META, ...ADMIN_TYPE_META] : TYPE_META;
  // Mostra solo le voci effettivamente presenti nei dati filtrati, ordinate
  // per importo decrescente. Cosi' la card riflette esattamente la tabella
  // commissioni a fianco (no righe "fantasma" a 0%).
  const types = allTypes
    .map((t) => ({ ...t, amount: Number(breakdown[t.key] || 0) }))
    .filter((t) => t.amount > 0)
    .sort((a, b) => b.amount - a.amount);

  return (
    <Card>
      <CardContent>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          {translate("userFinancial.eWallet.breakdownTitle")}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
          {translate("userFinancial.eWallet.breakdownSubtitle")}
        </Typography>

        {isLoading ? (
          <Stack spacing={1.5}>
            {[0, 1, 2].map((i) => (
              <Box key={i}>
                <Skeleton variant="text" width="100%" height={20} />
                <Skeleton variant="rectangular" width="100%" height={8} sx={{ borderRadius: 4, mt: 0.5 }} />
              </Box>
            ))}
          </Stack>
        ) : total <= 0 ? (
          <Box sx={{ py: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              {translate("userFinancial.eWallet.breakdownEmpty")}
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {types.map((t) => {
              const amount = t.amount;
              const pct = total > 0 ? Math.round((amount / total) * 100) : 0;
              return (
                <Box key={t.key}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                    <Typography variant="body2" sx={{ flexGrow: 1, fontWeight: 500 }}>
                      {t.emoji} {translate(t.labelKey)}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      <Currency>{fNumberCents(amount)}</Currency>
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 40, textAlign: "right" }}>
                      {pct}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={pct}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: theme.palette.action.hover,
                      "& .MuiLinearProgress-bar": {
                        borderRadius: 4,
                        backgroundColor: t.color,
                      },
                    }}
                  />
                </Box>
              );
            })}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default BreakdownByType;
